import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useLocale } from '../../contexts/LocaleContext';
import { API_BASE } from '../../config';
import { ArrowUpDown, ArrowUp, ArrowDown, RotateCcw, Trash2 } from 'lucide-react';
import { RotatingDeleteIcon } from '../RotatingDeleteIcon';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { navigationClickHandlers } from '../../utils/app-navigation';
import { AdminAccessDenied } from './AdminAccessDenied';
import { AdminErrorAlert, AdminEmptyState, AdminLoadingState, AdminTableWrap } from './admin-ui';
import { toBcp47Locale } from '../../locales/locale';

interface AdminUserRow {
  id: string;
  email: string;
  tier: 'free' | 'pro' | 'editorial';
  createdAt: string;
  deletedAt: string | null;
  inputTokens: number;
  outputTokens: number;
  creditBalance: number | null;
  lastDocumentUsageAt?: string | null;
}

const isDeletedUser = (deletedAt: string | null | undefined): boolean => Boolean(deletedAt);

const tierSortValue = (tier: AdminUserRow['tier']): number => {
  if (tier === 'free') return 0;
  if (tier === 'pro') return 1;
  return 2;
};

export const Users: React.FC = () => {
  const { user, token, masterKey } = useAuth();
  const navigate = useNavigate();
  const { t, locale } = useLocale();
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hideDeleted, setHideDeleted] = useState<boolean>(true);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [processingUserIds, setProcessingUserIds] = useState<Set<string>>(new Set());

  const fetchUsers = useCallback(async () => {
    if ((!token && !masterKey) || !user?.admin) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const url = masterKey
        ? `${API_BASE}/api/users?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/users`;
      const response = await fetch(url, {
        headers: {
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      setUsers(data.users || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(t('failed_to_load_users'));
    } finally {
      setLoading(false);
    }
  }, [token, user, masterKey, t]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSoftDelete = useCallback(
    async (userId: string, email: string) => {
      if (!user?.admin) return;
      if (!masterKey && user.id === userId) {
        setError(t('cannot_delete_own_account'));
        return;
      }
      if (!window.confirm(t('confirm_delete_user'))) {
        return;
      }

      setProcessingUserIds((prev) => new Set(prev).add(userId));
      try {
        const url = masterKey
          ? `${API_BASE}/api/users/${userId}?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/users/${userId}`;
        const response = await fetch(url, {
          method: 'DELETE',
          headers: {
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || 'Failed to delete user');
        }

        const data = await response.json();
        setHideDeleted(false);
        setUsers((prev) =>
          prev.map((u) =>
            u.id === userId
              ? { ...u, deletedAt: data.user?.deletedAt ?? new Date().toISOString() }
              : u
          )
        );
        await fetchUsers();
      } catch (err) {
        console.error(`Error deleting user ${email}:`, err);
        setError(t('failed_to_delete_user'));
      } finally {
        setProcessingUserIds((prev) => {
          const next = new Set(prev);
          next.delete(userId);
          return next;
        });
      }
    },
    [user, masterKey, token, t, fetchUsers]
  );

  const handleRestore = useCallback(
    async (userId: string, email: string) => {
      if (!user?.admin) return;

      setProcessingUserIds((prev) => new Set(prev).add(userId));
      try {
        const url = masterKey
          ? `${API_BASE}/api/users/${userId}/restore?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/users/${userId}/restore`;
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || 'Failed to restore user');
        }

        setUsers((prev) =>
          prev.map((u) => (u.id === userId ? { ...u, deletedAt: null } : u))
        );
        await fetchUsers();
      } catch (err) {
        console.error(`Error restoring user ${email}:`, err);
        setError(t('failed_to_restore_user'));
      } finally {
        setProcessingUserIds((prev) => {
          const next = new Set(prev);
          next.delete(userId);
          return next;
        });
      }
    },
    [user, masterKey, token, t, fetchUsers]
  );

  const formatDateTime = useCallback(
    (iso: string) =>
      new Date(iso).toLocaleDateString(toBcp47Locale(locale), {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
    [locale]
  );

  const visibleUsers = useMemo(
    () => users.filter((u) => !hideDeleted || !isDeletedUser(u.deletedAt)),
    [users, hideDeleted]
  );

  const columns = useMemo<ColumnDef<AdminUserRow>[]>(
    () => [
      {
        accessorKey: 'email',
        header: ({ column }) => (
          <button
            type="button"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="flex items-center gap-2 hover:bg-muted px-2 py-1 rounded"
          >
            {t('email')}
            {column.getIsSorted() === 'asc' ? (
              <ArrowUp className="h-4 w-4" />
            ) : column.getIsSorted() === 'desc' ? (
              <ArrowDown className="h-4 w-4" />
            ) : (
              <ArrowUpDown className="h-4 w-4" />
            )}
          </button>
        ),
        cell: ({ row }) => {
          const u = row.original;
          return (
            <div className="flex items-center gap-2">
              {isDeletedUser(u.deletedAt) && (
                <span title={`Deleted on ${new Date(u.deletedAt!).toLocaleDateString()}`} className="inline-flex shrink-0">
                  <Trash2 size={16} className="text-red-600" />
                </span>
              )}
              <span
                className={`font-medium ${
                  isDeletedUser(u.deletedAt) ? 'text-muted-foreground' : 'text-foreground'
                }`}
              >
                {u.email}
              </span>
            </div>
          );
        },
      },
      {
        accessorKey: 'tier',
        header: ({ column }) => (
          <button
            type="button"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="flex items-center gap-2 hover:bg-muted px-2 py-1 rounded"
          >
            {t('users_list_plan')}
            {column.getIsSorted() === 'asc' ? (
              <ArrowUp className="h-4 w-4" />
            ) : column.getIsSorted() === 'desc' ? (
              <ArrowDown className="h-4 w-4" />
            ) : (
              <ArrowUpDown className="h-4 w-4" />
            )}
          </button>
        ),
        cell: ({ row }) => {
          const tier = row.original.tier;
          const label =
            tier === 'pro' ? t('plan_name_pro') : tier === 'editorial' ? t('plan_name_editorial') : t('plan_name_free');
          return (
            <span
              className={`inline-block rounded-full px-3 py-1 text-xs font-medium capitalize ${
                tier === 'pro'
                  ? 'bg-blue-100 text-blue-900 dark:bg-blue-950 dark:text-blue-100'
                  : tier === 'editorial'
                    ? 'bg-purple-100 text-purple-900 dark:bg-purple-950 dark:text-purple-100'
                    : 'bg-muted text-muted-foreground'
              }`}
            >
              {label}
            </span>
          );
        },
        sortingFn: (rowA, rowB) =>
          tierSortValue(rowA.original.tier) - tierSortValue(rowB.original.tier),
      },
      {
        accessorKey: 'inputTokens',
        header: ({ column }) => (
          <button
            type="button"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="ml-auto flex w-full items-center justify-end gap-2 hover:bg-muted px-2 py-1 rounded"
          >
            {t('input_tokens')}
            {column.getIsSorted() === 'asc' ? (
              <ArrowUp className="h-4 w-4" />
            ) : column.getIsSorted() === 'desc' ? (
              <ArrowDown className="h-4 w-4" />
            ) : (
              <ArrowUpDown className="h-4 w-4" />
            )}
          </button>
        ),
        cell: ({ row }) => (
          <div className="text-right tabular-nums text-muted-foreground">{row.original.inputTokens.toLocaleString()}</div>
        ),
      },
      {
        accessorKey: 'outputTokens',
        header: ({ column }) => (
          <button
            type="button"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="ml-auto flex w-full items-center justify-end gap-2 hover:bg-muted px-2 py-1 rounded"
          >
            {t('output_tokens')}
            {column.getIsSorted() === 'asc' ? (
              <ArrowUp className="h-4 w-4" />
            ) : column.getIsSorted() === 'desc' ? (
              <ArrowDown className="h-4 w-4" />
            ) : (
              <ArrowUpDown className="h-4 w-4" />
            )}
          </button>
        ),
        cell: ({ row }) => (
          <div className="text-right tabular-nums text-muted-foreground">{row.original.outputTokens.toLocaleString()}</div>
        ),
      },
      {
        accessorKey: 'creditBalance',
        header: ({ column }) => (
          <button
            type="button"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="ml-auto flex w-full items-center justify-end gap-2 hover:bg-muted px-2 py-1 rounded"
          >
            {t('current_balance')}
            {column.getIsSorted() === 'asc' ? (
              <ArrowUp className="h-4 w-4" />
            ) : column.getIsSorted() === 'desc' ? (
              <ArrowDown className="h-4 w-4" />
            ) : (
              <ArrowUpDown className="h-4 w-4" />
            )}
          </button>
        ),
        cell: ({ row }) => {
          const b = row.original.creditBalance;
          return (
            <div className="text-right tabular-nums text-muted-foreground">{b !== null ? b.toLocaleString() : '-'}</div>
          );
        },
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.creditBalance;
          const b = rowB.original.creditBalance;
          if (a === null && b === null) return 0;
          if (a === null) return 1;
          if (b === null) return -1;
          return a - b;
        },
      },
      {
        accessorKey: 'createdAt',
        header: ({ column }) => (
          <button
            type="button"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="flex items-center gap-2 hover:bg-muted px-2 py-1 rounded"
          >
            {t('creation_date')}
            {column.getIsSorted() === 'asc' ? (
              <ArrowUp className="h-4 w-4" />
            ) : column.getIsSorted() === 'desc' ? (
              <ArrowDown className="h-4 w-4" />
            ) : (
              <ArrowUpDown className="h-4 w-4" />
            )}
          </button>
        ),
        cell: ({ row }) => <div className="text-muted-foreground">{formatDateTime(row.original.createdAt)}</div>,
        sortingFn: (rowA, rowB) =>
          new Date(rowA.original.createdAt).getTime() - new Date(rowB.original.createdAt).getTime(),
      },
      {
        id: 'lastDocumentUsageAt',
        accessorFn: (row) => row.lastDocumentUsageAt,
        header: ({ column }) => (
          <button
            type="button"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="flex items-center gap-2 hover:bg-muted px-2 py-1 rounded"
          >
            {t('last_usage')}
            {column.getIsSorted() === 'asc' ? (
              <ArrowUp className="h-4 w-4" />
            ) : column.getIsSorted() === 'desc' ? (
              <ArrowDown className="h-4 w-4" />
            ) : (
              <ArrowUpDown className="h-4 w-4" />
            )}
          </button>
        ),
        cell: ({ row }) => {
          const v = row.original.lastDocumentUsageAt ?? null;
          return (
            <div className="text-muted-foreground">{v ? formatDateTime(v) : '—'}</div>
          );
        },
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.lastDocumentUsageAt ?? null;
          const b = rowB.original.lastDocumentUsageAt ?? null;
          if (!a && !b) return 0;
          if (!a) return 1;
          if (!b) return -1;
          return new Date(a).getTime() - new Date(b).getTime();
        },
      },
      {
        id: 'actions',
        header: () => <span className="whitespace-nowrap">{t('actions')}</span>,
        cell: ({ row }) => {
          const u = row.original;
          const isProcessing = processingUserIds.has(u.id);
          const isSelf = !masterKey && user?.id === u.id;
          const deleted = isDeletedUser(u.deletedAt);

          if (deleted) {
            return (
              <div
                className="flex justify-end"
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={isProcessing}
                  onClick={() => handleRestore(u.id, u.email)}
                  className="gap-1.5 whitespace-nowrap"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  {t('restore_deleted_user')}
                </Button>
              </div>
            );
          }

          return (
            <div
              className="flex justify-end"
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <Button
                type="button"
                variant="destructive"
                size="sm"
                disabled={isProcessing || isSelf}
                onClick={() => handleSoftDelete(u.id, u.email)}
                className="group gap-1.5 whitespace-nowrap"
                title={isSelf ? t('cannot_delete_own_account') : undefined}
              >
                <RotatingDeleteIcon className="h-3.5 w-3.5" />
                {t('delete_user')}
              </Button>
            </div>
          );
        },
      },
    ],
    [t, locale, formatDateTime, processingUserIds, user, masterKey, handleSoftDelete, handleRestore]
  );

  const table = useReactTable({
    data: visibleUsers,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  if (!user || !user.admin) {
    return <AdminAccessDenied />;
  }

  if (loading) {
    return <AdminLoadingState label={t('loading_users')} />;
  }

  return (
    <div className="flex w-full min-w-0 flex-col gap-6">
      {error && <AdminErrorAlert>{error}</AdminErrorAlert>}

      <div className="flex items-center gap-2">
        <Checkbox
          id="hideDeletedUsers"
          checked={hideDeleted}
          onCheckedChange={(v) => setHideDeleted(v === true)}
        />
        <Label htmlFor="hideDeletedUsers" className="cursor-pointer text-sm font-normal text-foreground">
          {t('hide_deleted_users')}
        </Label>
      </div>

      {visibleUsers.length === 0 ? (
        <AdminEmptyState>{t('no_users_registered')}</AdminEmptyState>
      ) : (
        <AdminTableWrap>
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      className={
                        header.column.id === 'inputTokens' ||
                        header.column.id === 'outputTokens' ||
                        header.column.id === 'creditBalance'
                          ? 'text-right'
                          : header.column.id === 'actions'
                            ? 'text-right whitespace-nowrap'
                            : ''
                      }
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className={`cursor-pointer ${isDeletedUser(row.original.deletedAt) ? 'opacity-70' : ''}`}
                  {...navigationClickHandlers(navigate, `/admin/users/${row.original.id}`)}
                  aria-label={`${t('user_details')}: ${row.original.email}`}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={
                        cell.column.id === 'inputTokens' ||
                        cell.column.id === 'outputTokens' ||
                        cell.column.id === 'creditBalance'
                          ? 'text-right'
                          : cell.column.id === 'actions'
                            ? 'text-right whitespace-nowrap'
                            : ''
                      }
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </AdminTableWrap>
      )}
    </div>
  );
};
